import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Textarea } from "./kd";

let cache: Promise<any[]> | null = null;
export const reloadBausteine = () => { cache = null; };

export function useBausteine() {
  const [list, setList] = useState<any[]>([]);
  useEffect(() => {
    if (!cache) cache = api.get("/textbausteine").then((r) => r.data).catch(() => { cache = null; return []; });
    cache.then(setList);
  }, []);
  return list;
}

type Props = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> & { value: string; onValue: (v: string) => void };

/** Textfeld mit Textbausteinen: „/kürzel“ tippen, dann Enter/Tab oder Klick. */
export function SnippetTextarea({ value, onValue, ...rest }: Props) {
  const list = useBausteine();
  const ref = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(0);
  const [active, setActive] = useState(0);
  const [closed, setClosed] = useState(false);
  const m = /(^|\s)\/([\wäöüß-]*)$/i.exec(value.slice(0, caret));
  const matches = m && !closed ? list.filter((b) => b.kuerzel.startsWith(m[2].toLowerCase())).slice(0, 6) : [];
  const insert = (b: any) => {
    const start = caret - (m ? m[2].length + 1 : 0);
    const next = value.slice(0, start) + b.text + value.slice(caret);
    onValue(next);
    const pos = start + b.text.length;
    requestAnimationFrame(() => { ref.current?.focus(); ref.current?.setSelectionRange(pos, pos); setCaret(pos); });
  };
  const track = (e: any) => setCaret(e.target.selectionStart ?? 0);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!matches.length) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a + (e.key === "ArrowDown" ? 1 : matches.length - 1)) % matches.length); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insert(matches[Math.min(active, matches.length - 1)]); }
    else if (e.key === "Escape") setClosed(true);
  };
  return (
    <div className="relative">
      <Textarea ref={ref} {...rest} value={value} onKeyDown={onKeyDown} onKeyUp={track} onClick={track}
        onChange={(e) => { onValue(e.target.value); setCaret(e.target.selectionStart ?? 0); setActive(0); setClosed(false); }} />
      {matches.length > 0 && (
        <ul role="listbox" className="absolute left-0 right-0 z-30 mt-1 max-h-64 overflow-y-auto rounded-lg border border-border bg-surface shadow-card" data-testid="snippet-menu">
          {matches.map((b, i) => (
            <li key={b.id} role="option" aria-selected={i === active}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); insert(b); }} data-testid={`snippet-option-${b.kuerzel}`}
                className={`block w-full px-3 py-2 text-left transition-colors ${i === active ? "bg-primary-light" : "hover:bg-bg"}`}>
                <span className="font-medium">/{b.kuerzel}</span> <span className="text-sm text-text-muted">{b.titel}</span>
                <span className="block truncate text-sm text-text-muted">{b.text}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {list.length > 0 && <p className="mt-1 text-xs text-text-muted">Tipp: „/“ tippen für Textbausteine</p>}
    </div>
  );
}
